import { useEffect, useMemo, useState } from "react"
import { Button, Card, Col, Flex, Row, Spin, Tooltip, message } from "antd"
import dayjs from "dayjs"
import * as com from "../Common"
import { State, LoanOverview } from "../State"
import { createNewApplication } from "../newContent/api/api"
import TopNavBar from "../newContent/page/TopNavbar"
import "../newContent/page/styles/loans.css"
import { getLoanAmount } from "../Tools/Loan"
import { camelToTitleCase } from "../Tools/String"
import { APPLICATION_V2_CONTINUE_PATH, getLoansV2, switchLoanV2 } from "./api/loansV2Client"
import { isRedirectingToLogoutOnUnauthorized, UNAUTHORIZED_SESSION_MESSAGE } from "./api/unauthorizedRedirect"
import { listPendingCreditPullPaymentRequestsV2 } from "./api/creditPullPaymentV2Client"
import { useHistory } from "react-router-dom"
import { flushThenLeave, track } from "../statsig"
import { PAGE, STATSIG } from "../statsig/events"

const formatLoanStatusLabel = (currentStatus) => (
    currentStatus
        ? (com.CurrentStatusUtils?.getStatusName?.(currentStatus) || camelToTitleCase(currentStatus))
        : "In progress"
)

const loanPurposeMap = {
    purchase: "Purchase",
    purchaserehab: "Purchase and Rehab",
    refinance: "Refinance",
    cashoutrefinance: "Cash-out Refinance",
}

const LoanTypeMap = {
    rehab: "Rehab",
    groundup: "Ground Up Construction",
    rental: "Rental",
    bridge: "Bridge",
}

const displayAddress = (loan) => {
    if (!loan?.address && !loan?.county) {
        return "TBD"
    }
    if (loan.address) {
        return loan.address
    }
    return `${com.removeCountySuffix(loan.county || "")}, ${com.convertStateToAbbr(loan.state || "")}`
}

const toAmountNumber = (value) => {
    if (value == null || value === "") return value
    const n = Number(value)
    return Number.isFinite(n) ? n : value
}

const displayLoanAmount = (loan) => (
    getLoanAmount(
        loan.purpose,
        loan.mortgageApplied,
        loan.salesPrice,
        toAmountNumber(loan.downPayment),
        toAmountNumber(loan.initialLoanAmount),
        toAmountNumber(loan.refinanceLoanAmount),
        loan.loanSize,
        loan.hasContract,
        loan.appraisal,
        loan.hardMoneyLoanType,
        toAmountNumber(loan.rehabOrConstructionLoanAmount),
    )
)

const HARD_MONEY_INTERVIEW_PATH = "/application/hardMoneyLoan/interview/main/borrower"

const loanPurposeTitle = (loan) => {
    const purpose = loan.purpose
    if (purpose == null || purpose === "") {
        return "Loan Purpose TBD"
    }
    if (loan.mortgageApplied === "hardmoneyloan") {
        let loanTypeText = "TBD"
        const loanType = loan.hardMoneyLoanType
        if (loanType && loanType !== "fixflip") {
            loanTypeText = LoanTypeMap[loanType] || loanType
        }
        return `Hard Money - ${loanPurposeMap[purpose] || purpose} - ${loanTypeText}`
    }
    if (loan.mortgageApplied === "nonqm") {
        return `Non QM - ${loanPurposeMap[purpose] || purpose}`
    }
    return `Home ${loanPurposeMap[purpose] || purpose}`
}

const LoansV2 = () => {
    const history = useHistory()
    const isMobile = window.innerWidth < 768
    const isHardMoneyCustomer = sessionStorage.getItem("isHardMoneyLoanCustomer") === "true"

    const [loading, setLoading] = useState(true)
    const [switchingLoanId, setSwitchingLoanId] = useState("")
    const [creating, setCreating] = useState(false)
    const [currentLoanId, setCurrentLoanId] = useState("")
    const [borrowerFirstName, setBorrowerFirstName] = useState("")
    const [borrowerLastName, setBorrowerLastName] = useState("")
    const [borrowerEmail, setBorrowerEmail] = useState("")
    const [loans, setLoans] = useState([])
    const [pendingPaymentsByLoan, setPendingPaymentsByLoan] = useState({})

    const sortedLoans = useMemo(() => {
        const touchOrStart = (loan) => {
            const raw = loan.lastTouch || loan.start || ""
            const t = dayjs(raw)
            return t.isValid() ? t.valueOf() : 0
        }
        const next = [...loans]
        next.sort((a, b) => touchOrStart(b) - touchOrStart(a))
        next.sort((a, b) => (currentLoanId === a.loanId ? -1 : currentLoanId === b.loanId ? 1 : 0))
        return next
    }, [loans, currentLoanId])

    const loadLoans = async () => {
        setLoading(true)
        try {
            const [body, pendingBody] = await Promise.all([
                getLoansV2(),
                listPendingCreditPullPaymentRequestsV2().catch(() => ({ Requests: [] })),
            ])
            setCurrentLoanId(body.currentLoanId || "")
            setBorrowerFirstName(body.borrowerFirstName || "")
            setBorrowerLastName(body.borrowerLastName || "")
            setBorrowerEmail(body.borrowerEmail || "")
            setLoans(Array.isArray(body.loans) ? body.loans : [])
            const byLoan = {}
            ;(pendingBody.Requests || []).forEach((item) => {
                if (item?.loanId && !byLoan[item.loanId]) {
                    byLoan[item.loanId] = item
                }
            })
            setPendingPaymentsByLoan(byLoan)
        } catch (err) {
            if (isRedirectingToLogoutOnUnauthorized()) {
                message.warning(UNAUTHORIZED_SESSION_MESSAGE)
                return
            }
            message.error(err.message || "Failed to load loans")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadLoans()
    }, [])

    useEffect(() => {
        track(STATSIG.COMMON.VIEW, PAGE.LOANS, { page: PAGE.LOANS })
    }, [])

    const openLoan = async (loan) => {
        if (!loan?.loanId) {
            return
        }
        setSwitchingLoanId(loan.loanId)
        try {
            const body = await switchLoanV2(loan.loanId)
            localStorage.setItem("LOAN_OFFICER_NAME", loan.loanOfficerName || "")
            localStorage.setItem("LOAN_CURRENT_STAGE", loan.currentStage || "")
            localStorage.setItem("LOAN_CURRENT_STATUS", loan.currentStatus || "")
            localStorage.setItem("LOAN_ADDRESS", displayAddress(loan))

            // V2 interview loads from split tables; clear legacy whole-state cache.
            sessionStorage.removeItem("state")
            sessionStorage.removeItem("originalstate")

            const mortgageApplied = body.mortgageApplied || loan.mortgageApplied || ""
            const targetLink = mortgageApplied === "hardmoneyloan"
                ? HARD_MONEY_INTERVIEW_PATH
                : APPLICATION_V2_CONTINUE_PATH

            track(STATSIG.LOANS.LOAN_OPEN, loan.loanId, {
                source: "list",
                target_link: targetLink,
            })

            if (mortgageApplied === "hardmoneyloan") {
                sessionStorage.setItem("isHardMoneyLoanCustomer", "true")
                await flushThenLeave(() => {
                    window.location.href = HARD_MONEY_INTERVIEW_PATH
                })
                return
            }

            // Full reload so V2 interview boots without leftover Redux/session whole-state.
            await flushThenLeave(() => {
                window.location.href = APPLICATION_V2_CONTINUE_PATH
            })
        } catch (err) {
            if (isRedirectingToLogoutOnUnauthorized()) {
                message.warning(UNAUTHORIZED_SESSION_MESSAGE)
            } else {
                message.error(err.message || "Failed to switch loan")
            }
        } finally {
            setSwitchingLoanId("")
        }
    }

    const openNewApplication = async () => {
        track(STATSIG.LOANS.CREATE_NEW_LOAN_BUTTON, "create_manually", {
            type: "create_manually",
        })
        setCreating(true)
        try {
            const newS = JSON.parse((() => {
                const a = new State()
                return a.toJson()
            })())
            if (isHardMoneyCustomer) {
                newS.application.product.mortgageapplied = "hardmoneyloan"
                newS.application.property.purpose = "purchase"
                newS.application.property.hascontract = true
            }
            newS.progress.interview.step = 0
            newS.progress.interview.max = 0
            newS.progress.lasttouch = new Date().toLocaleString()
            newS.application.loanOverview = new LoanOverview()
            newS.application.loanOverview.receivedDate = new Date().toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
            })

            const data = {
                LoanID: currentLoanId,
                NewState: com.ascii2hex(JSON.stringify(newS)),
            }
            // createNewApplication 成功会打 LOAN_INSERT（source=my_loans_v2）
            const { Status, Text } = await createNewApplication(data, "my_loans_v2")
            if (Status !== "OK") {
                throw new Error(Text || "Failed to create application")
            }

            // Match legacy My Loans: seed session for PreApp / HM, then navigate.
            sessionStorage.setItem("state", JSON.stringify(newS))
            sessionStorage.setItem("originalstate", JSON.stringify(newS))

            if (isHardMoneyCustomer) {
                await flushThenLeave(() => {
                    window.location.href = HARD_MONEY_INTERVIEW_PATH
                })
                return
            }
            // Logged-in PreApp collects purpose then continues into Application V2.
            await flushThenLeave(() => {
                window.location.href = "/services/preapp?customerid=" + com.getCustomerId()
            })
        } catch (err) {
            message.error(err.message || "Failed to create application")
        } finally {
            setCreating(false)
        }
    }

    const renderTitle = (loan) => (
        <div
            style={isMobile
                ? { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12, padding: "13px 0" }
                : { display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
            <div className="d-flex align-items-center">
                <div>
                    {loan.purpose !== "purchase" ? (
                        <img style={isMobile ? { width: 24, height: 24 } : {}} src="/money.svg" alt="money" />
                    ) : (
                        <img style={isMobile ? { width: 24, height: 24 } : {}} src="/house.svg" alt="house" />
                    )}
                </div>
                <div className="ml-2" style={{ fontWeight: 500, color: "#333", fontSize: 16 }}>
                    {loanPurposeTitle(loan)}
                    <span className="ml-1">{!isMobile ? "·" : ""}</span>
                    <span
                        style={isMobile
                            ? { display: "block", color: "#6E6E70", fontWeight: 400, fontSize: 16 }
                            : { color: "#6E6E70", fontWeight: 400, fontSize: 16 }}
                    >
                        {loan.lastTouch || loan.start
                            ? dayjs(loan.lastTouch || loan.start).format("MM/DD/YYYY HH:mm A")
                            : "--"}
                    </span>
                </div>
            </div>
            <div
                style={{
                    height: "30px",
                    padding: "5px 10px",
                    borderRadius: "15px",
                    backgroundColor: "#D7E2FB",
                    color: "#1630AC",
                    fontSize: "14px",
                    fontWeight: 600,
                }}
            >
                {formatLoanStatusLabel(loan.currentStatus)}
            </div>
        </div>
    )

    return (
        <div style={{ background: "white" }}>
            <TopNavBar
                firstName={borrowerFirstName}
                lastName={borrowerLastName}
                email={borrowerEmail}
            />
            <Spin spinning={loading || creating || !!switchingLoanId}>
                <Flex gap="middle" wrap>
                    <Row className="w-100">
                        <Col span={24}>
                            <div className="LoansContent">
                                <div style={isMobile ? { fontSize: 24 } : {}} className="LoansTitle text-left">
                                    Welcome back{borrowerFirstName ? `, ${borrowerFirstName}` : ""}
                                </div>
                                <div
                                    className={isMobile
                                        ? "w-100"
                                        : "w-100 d-flex justify-content-between text-left mt-4 align-items-center"}
                                >
                                    <div>
                                        <div className="YourLoans">Your loans</div>
                                        <div className="loansIntroduce">
                                            Here is an overview of your current and past loans. Select an application to continue.
                                        </div>
                                    </div>
                                    <div className={isMobile ? "mt-3" : ""}>
                                        <Button
                                            style={{
                                                width: "152px",
                                                height: "46px",
                                                borderRadius: "6px",
                                                border: "1px solid #E3EAFD",
                                                backgroundColor: "#F7F8FF",
                                                fontSize: "16px",
                                                fontWeight: 600,
                                                color: "#325CEB",
                                            }}
                                            onClick={openNewApplication}
                                            disabled={creating}
                                        >
                                            New application
                                        </Button>
                                    </div>
                                </div>

                                <Col span={24}>
                                    {sortedLoans.map((loan) => (
                                        <Card key={loan.loanId} className="mt-4" type="inner" title={renderTitle(loan)}>
                                            {pendingPaymentsByLoan[loan.loanId] && (
                                                <div className="credit-pull-payment-banner">
                                                    <div className="credit-pull-payment-banner-text">
                                                        Your loan officer requested a credit report. Your application continues either way — this step waits on payment.
                                                    </div>
                                                    <Button
                                                        type="primary"
                                                        className="credit-pull-payment-banner-btn"
                                                        onClick={() => {
                                                            const cid = (com.getCustomerId() || "").trim()
                                                            const id = pendingPaymentsByLoan[loan.loanId].id
                                                            const q = cid ? `?customerid=${encodeURIComponent(cid)}` : ""
                                                            track(STATSIG.PAYMENT.OPEN, "credit_pull", {
                                                                type: "credit_pull",
                                                                source: "my_loans",
                                                                request_id: id,
                                                                loan_id: loan.loanId || "",
                                                            })
                                                            history.push(`/application/credit-report-payment/${id}${q}`)
                                                        }}
                                                    >
                                                        {`Pay $${Number(pendingPaymentsByLoan[loan.loanId].amount || 0).toFixed(2)}`}
                                                    </Button>
                                                </div>
                                            )}
                                            <Row className="align-items-center justify-content-between">
                                                {!isMobile && (
                                                    <Row className="justify-content-between" style={{ width: "100%" }}>
                                                        <div style={{ width: "30%" }}>
                                                            <Row className="justify-content-between align-items-start">
                                                                <Col>
                                                                    <div style={{ color: "#6E6E70", fontWeight: 500, fontSize: 14 }}>
                                                                        Property address
                                                                    </div>
                                                                    <Tooltip placement="top" title={displayAddress(loan)}>
                                                                        <div className="dataLoansText overflowH" style={{ width: 200 }}>
                                                                            {displayAddress(loan)}
                                                                        </div>
                                                                    </Tooltip>
                                                                </Col>
                                                                <Col>
                                                                    <div style={{ color: "#6E6E70", fontWeight: 500, fontSize: 14 }}>
                                                                        Property type
                                                                    </div>
                                                                    <div className="dataLoansText">
                                                                        {!loan.propertyType ? "--" : com.getPropertyTypeName(loan.propertyType)}
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                        <div style={{ width: "30%" }}>
                                                            <Row className="justify-content-between align-items-start">
                                                                <Col>
                                                                    <div style={{ color: "#6E6E70", fontWeight: 500, fontSize: 14 }}>
                                                                        Loan officer
                                                                    </div>
                                                                    <div className="dataLoansText">
                                                                        {!loan.loanOfficerName ? "N/A" : loan.loanOfficerName}
                                                                    </div>
                                                                </Col>
                                                                <Col>
                                                                    <div style={{ color: "#6E6E70", fontWeight: 500, fontSize: 14 }}>
                                                                        Loan amount
                                                                    </div>
                                                                    <div className="dataLoansText">
                                                                        {displayLoanAmount(loan)}
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                        <Col className="d-flex justify-content-end align-items-center">
                                                            <Button
                                                                style={{ height: 40, width: 93, fontSize: 16, fontWeight: 600, borderRadius: 6 }}
                                                                type="primary"
                                                                loading={switchingLoanId === loan.loanId}
                                                                onClick={() => openLoan(loan)}
                                                            >
                                                                {currentLoanId === loan.loanId ? "Continue" : "View"}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                                {isMobile && (
                                                    <>
                                                        <div style={{ width: "100%" }}>
                                                            <Col className="d-flex align-items-center justify-content-between borderBottom">
                                                                <div style={{ color: "#333", fontWeight: 500, fontSize: 16 }}>
                                                                    {!loan.address ? "--" : loan.address}
                                                                </div>
                                                            </Col>
                                                            <Col className="d-flex align-items-center justify-content-between borderBottom">
                                                                <div style={{ color: "#6E6E70", width: "60%", fontWeight: 500, fontSize: 14 }}>
                                                                    Property type
                                                                </div>
                                                                <div className="MDataLoansText">
                                                                    {!loan.propertyType ? "--" : com.getPropertyTypeName(loan.propertyType)}
                                                                </div>
                                                            </Col>
                                                        </div>
                                                        <div style={{ width: "100%" }}>
                                                            <Col className="d-flex align-items-center justify-content-between borderBottom">
                                                                <div style={{ color: "#6E6E70", width: "60%", fontWeight: 500, fontSize: 14 }}>
                                                                    Loan officer
                                                                </div>
                                                                <div className="MDataLoansText">{loan.loanOfficerName || "N/A"}</div>
                                                            </Col>
                                                            <Col className="d-flex align-items-center justify-content-between borderBottom">
                                                                <div style={{ color: "#6E6E70", width: "60%", fontWeight: 500, fontSize: 14 }}>
                                                                    Loan amount
                                                                </div>
                                                                <div className="MDataLoansText">{displayLoanAmount(loan)}</div>
                                                            </Col>
                                                            <Col style={{ marginTop: 30 }}>
                                                                <Button
                                                                    className="loan-button-mobile"
                                                                    type="primary"
                                                                    loading={switchingLoanId === loan.loanId}
                                                                    onClick={() => openLoan(loan)}
                                                                >
                                                                    {currentLoanId === loan.loanId ? "Continue" : "View"}
                                                                </Button>
                                                            </Col>
                                                        </div>
                                                    </>
                                                )}
                                            </Row>
                                        </Card>
                                    ))}
                                    {!loading && sortedLoans.length === 0 && (
                                        <div className="mt-4 text-left" style={{ color: "#6E6E70" }}>
                                            No loans found for this account.
                                        </div>
                                    )}
                                </Col>
                            </div>
                        </Col>
                    </Row>
                </Flex>
            </Spin>
        </div>
    )
}

export default LoansV2
